import clsx from "clsx";
import React, { useCallback, useMemo, useState } from "react";
import styles from "./ImageMagnifier.module.scss";

type Props = {
  src: string;
  width?: string;
  height?: string;
  magnifierHeight?: number;
  magnifieWidth?: number;
  zoomLevel?: number;
};

export const ImageMagnifier: React.FC<React.PropsWithChildren<Props>> = ({
  src,
  width,
  height,
  magnifierHeight = 200,
  magnifieWidth = 200,
  zoomLevel = 1.9
}) => {
  const [[x, y], setXY] = useState([0, 0]);
  const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
  const [showMagnifier, setShowMagnifier] = useState(false);

  const onMouseEnter = useCallback((e) => {
    // update image size and turn-on magnifier
    const elem = e.currentTarget;
    const { width, height } = elem.getBoundingClientRect();
    setSize([width, height]);
    setShowMagnifier(true);
  }, []);

  const onMouseMove = useCallback((e) => {
    // update cursor position
    const elem = e.currentTarget;
    const { top, left } = elem.getBoundingClientRect();

    // calculate cursor position on the image
    const x = e.pageX - left - window.pageXOffset;
    const y = e.pageY - top - window.pageYOffset;
    setXY([x, y]);
  }, []);

  const onMouseLeave = useCallback(() => {
    // close magnifier
    setShowMagnifier(false);
  }, []);

  const magnifierStyle = useMemo(
    () => ({
      // set size of magnifier
      height: `${magnifierHeight}px`,
      width: `${magnifieWidth}px`,
      // move element center to cursor pos
      top: `${y - magnifierHeight / 2}px`,
      left: `${x - magnifieWidth / 2}px`,
      backgroundImage: `url('${src}')`,

      //calculate zoomed image size
      backgroundSize: `${imgWidth * zoomLevel}px ${imgHeight * zoomLevel}px`,

      //calculate position of zoomed image.
      backgroundPositionX: `${-x * zoomLevel + magnifieWidth / 2}px`,
      backgroundPositionY: `${-y * zoomLevel + magnifierHeight / 2}px`
    }),
    [imgHeight, imgWidth, magnifieWidth, magnifierHeight, src, x, y, zoomLevel]
  );

  return (
    <div className={styles.container} aria-hidden>
      <img
        src={src}
        style={{ height: height, width: width }}
        onMouseEnter={onMouseEnter}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        alt="Deck Plan"
      />

      <div
        aria-hidden
        className={clsx(styles.magnifier, { [styles.visible]: showMagnifier })}
        style={magnifierStyle}
      ></div>
    </div>
  );
};
